import type {
	AnyFilterDef,
	FilterModifiersOfFilterDef,
	FilterValueOfFilterDef,
	ValueOfFilterDef,
} from "../defs/BaseFilterDef"
import type { AnyFilterManager } from "../manager"
import type { AnyFilter } from "./typings"

export interface FilterConfig<TFilterDef extends AnyFilterDef> {
	id: string
	filterValue: FilterValueOfFilterDef<TFilterDef> | undefined
	filterModifier: FilterModifiersOfFilterDef<TFilterDef>
	filterDef: TFilterDef
}

export class Filter<TFilterDef extends AnyFilterDef> {
	readonly id: string
	private filterValue: FilterValueOfFilterDef<TFilterDef> | undefined
	private filterModifier: FilterModifiersOfFilterDef<TFilterDef>
	readonly filterDef: TFilterDef

	private subscribers: Set<(filter: AnyFilter) => void> = new Set()

	constructor(config: FilterConfig<TFilterDef>) {
		const { id, filterValue, filterModifier, filterDef } = config

		this.id = id
		this.filterValue = filterValue
		this.filterModifier = filterModifier
		this.filterDef = filterDef
	}

	subscribe = (callback: (filter: AnyFilter) => void) => {
		this.subscribers.add(callback)

		return () => {
			this.subscribers.delete(callback)
		}
	}

	private notifySubscribers = () => {
		for (const subscriber of this.subscribers) {
			subscriber(this)
		}
	}

	getFilterValue = () => {
		return this.filterValue
	}

	setFilterValue = (filterValue: FilterValueOfFilterDef<TFilterDef> | undefined) => {
		const modifier = this.filterDef.modifiers[this.filterModifier]

		if (filterValue !== undefined && !modifier.isFilterValueValid(filterValue)) {
			console.warn(`Setting filterValue: Filter value is not valid for modifier ${this.getFilterModifier()}`)
			return
		}

		this.filterValue = filterValue
		this.notifySubscribers()
	}

	getFilterModifier = () => {
		return this.filterModifier
	}

	setFilterModifier = (filterModifier: FilterModifiersOfFilterDef<TFilterDef>) => {
		const modifier = this.filterDef.modifiers[filterModifier]
		const filterValue = this.getFilterValue()

		if (filterValue && !modifier.isFilterValueValid(filterValue)) {
			this.filterValue = undefined
		}

		this.filterModifier = filterModifier
		this.notifySubscribers()
	}

	render = (filterManager: AnyFilterManager) => {
		return this.filterDef.render({
			filterDef: this.filterDef,
			filter: this,
			filterManager,
		})
	}

	getApplyFn = () => {
		const modifier = this.filterDef.modifiers[this.getFilterModifier()]
		const filterValue = this.getFilterValue()

		const filterValueValid = filterValue !== undefined && modifier.isFilterValueValid(filterValue)
		const applyFn = filterValueValid ? modifier.modifier.getApplyFn(filterValue) : null

		// TODO: Could consider applying some memoization to the filtering data process for additional speed gains
		return (value: ValueOfFilterDef<TFilterDef>) => {
			if (!filterValueValid) return true
			if (!modifier.isValueValid(value)) return false
			return applyFn!(value)
		}
	}
}
